import { useState, useEffect } from 'react';
import { Users, Award, Shield, Zap, TrendingUp, Target, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AboutPage = () => {
  const [stats, setStats] = useState({
    customers: 12500,
    projects: 3420,
    installers: 280,
    years: 8
  });

  useEffect(() => {
    // انیمیشن شمارنده
    const interval = setInterval(() => {
      setStats(prev => ({
        customers: Math.min(prev.customers + 500, 12500),
        projects: Math.min(prev.projects + 100, 3420),
        installers: Math.min(prev.installers + 10, 280),
        years: 8
      }));
    }, 30);
    
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Shield, title: 'ضمانت کیفیت', description: 'همه محصولات دارای گارانتی اصالت و کیفیت هستند' },
    { icon: Users, title: 'تیم حرفه‌ای', description: 'تیمی از متخصصان باتجربه در کنار شما' },
    { icon: Zap, title: 'انرژی پاک', description: 'کمک به حفظ محیط زیست با انرژی خورشیدی' },
    { icon: TrendingUp, title: 'بازدهی بالا', description: 'بهترین بازدهی با جدیدترین تکنولوژی‌ها' },
    { icon: Award, title: 'نماد اعتماد', description: 'دارای نماد اعتماد الکترونیکی' },
    { icon: Target, title: 'رضایت مشتری', description: 'رضایت 98 درصدی مشتریان' }
  ];

  const team = [
    { name: 'دکتر محمد حسینی', role: 'مدیرعامل', image: 'https://randomuser.me/api/portraits/men/1.jpg' },
    { name: 'مهندس سعید کریمی', role: 'مدیر فنی', image: 'https://randomuser.me/api/portraits/men/2.jpg' },
    { name: 'مهندس ندا احمدی', role: 'مدیر فروش', image: 'https://randomuser.me/api/portraits/women/3.jpg' },
    { name: 'مهندس رضا محمدی', role: 'مدیر پروژه', image: 'https://randomuser.me/api/portraits/men/4.jpg' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">درباره سان ایران پلتفرم</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            ما با هدف ترویج انرژی پاک و کاهش وابستگی به سوخت‌های فسیلی، پلتفرمی جامع برای خرید و نصب تجهیزات خورشیدی ایجاد کرده‌ایم.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">ماموریت ما</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
             سان ایران پلتفرم با ایجاد ارتباط مستقیم بین مشتریان، فروشندگان و نصاب‌های حرفه‌ای، فرآیند خرید و نصب سیستم‌های خورشیدی را ساده، شفاف و مقرون‌به‌صرفه کرده است. ما به آینده‌ای روشن با انرژی پاک ایمان داریم.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center min-w-[150px]">
                <div className="text-3xl font-bold text-blue-600">{stats.customers.toLocaleString()}+</div>
                <div className="text-sm text-gray-600">مشتری راضی</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center min-w-[150px]">
                <div className="text-3xl font-bold text-green-600">{stats.projects.toLocaleString()}+</div>
                <div className="text-sm text-gray-600">پروژه موفق</div>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 text-center min-w-[150px]">
                <div className="text-3xl font-bold text-yellow-600">{stats.installers}+</div>
                <div className="text-sm text-gray-600">نصاب حرفه‌ای</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center min-w-[150px]">
                <div className="text-3xl font-bold text-purple-600">{stats.years}+</div>
                <div className="text-sm text-gray-600">سال تجربه</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">چرا سان ایران پلتفرم؟</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 text-center hover:shadow-md transition">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">چگونه کار می‌کند؟</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'ثبت نام', desc: 'ثبت نام رایگان در پلتفرم', icon: '📝' },
              { step: 2, title: 'جستجو و انتخاب', desc: 'مقایسه محصولات و نصاب‌ها', icon: '🔍' },
              { step: 3, title: 'ثبت سفارش', desc: 'خرید و درخواست نصب', icon: '🛒' },
              { step: 4, title: 'نصب و راه‌اندازی', desc: 'پروژه توسط نصاب حرفه‌ای', icon: '⚡' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
                  {item.icon}
                </div>
                <div className="text-2xl font-bold text-blue-600 mb-2">{item.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">تیم ما</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 text-center">
                <img src={member.image} alt={member.name} className="w-32 h-32 rounded-full mx-auto mb-4 object-cover" />
                <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                <p className="text-gray-500 text-sm">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">آماده شروع هستید؟</h2>
          <p className="text-xl text-blue-100 mb-8">همین حالا به جمع کاربران سان ایران پلتفرم بپیوندید</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100">
              ثبت نام رایگان
            </Link>
            <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700">
              تماس با ما
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};